import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import bannerImage from '../../../../assets/newAssets/sliders/people.jpg';

const BANNER_IMAGE = bannerImage;

/* ------------------------------------------------------------------
   Layout idea (matches the design frame):
   The circle is a TRUE circle (width === height) but its diameter is
   LARGER than the band height, so the section clips it at the top and
   bottom. Two rules make it work:
     1. .ten-years-inner has a fixed height  -> the band height
     2. BannerSection has overflow: hidden   -> does the clipping
   Keep circle diameter ≈ 1.23 × band height to match the design.

   The title breaks onto two lines via a {br} placeholder in the
   translation strings — same pattern as HeroSection — so EN and ES
   both break where the design says, not wherever the box runs out.
------------------------------------------------------------------ */

const BannerSection = styled.section`
    width: 100%;
    background: var(--Regenera-Sand, #efe7da);
    font-family: Montserrat, sans-serif;
    padding: 0 48px;
    margin: 0;
    overflow: hidden; /* clips the circle top + bottom */

    .ten-years-inner {
        max-width: 1280px;
        margin: 0 auto;
        height: 340px; /* band height */
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 56px;
    }

    /* ---------- Photograph (true circle, bleeds past the band) ---------- */
    .ten-years-media {
        flex: 0 0 420px;
        width: 420px;
        height: 420px; /* === width, always. Bigger than the 340px band. */
        border-radius: 50%;
        overflow: hidden;
    }

    .ten-years-media img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        /* Framing knobs: object-position slides the crop, scale zooms in.
           transform-origin: left center keeps the left-hand man pinned
           while the group grows toward the right edge. */
        object-position: center 45%;
        transform: scale(1.12);
        transform-origin: left center;
    }

    .ten-years-media--empty {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 16px;
        border: 2px dashed var(--Trust-Green, #004d37);
        color: var(--Trust-Green, #004d37);
        font-size: 14px;
        line-height: 20px;
        opacity: 0.6;
    }

    /* ---------- Copy ---------- */
    .ten-years-content {
        flex: 1 1 auto;
        max-width: 640px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        text-align: center;
    }

    .ten-years-title {
        margin: 0;
        color: var(--Trust-Green, #004d37);
        text-align: center;
        font-family: Montserrat, sans-serif;
        font-size: 40px;
        font-style: normal;
        font-weight: 500;
        line-height: 52px;
    }

    .ten-years-subtitle {
        margin: 0;
        color: var(--Trust-Green, #004d37);
        text-align: center;
        font-family: Montserrat, sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 180%;
    }

    /* ---------- CTA ---------- */
    .ten-years-cta {
        display: inline-flex;
        padding: 8px 39px;
        justify-content: center;
        align-items: flex-start;
        gap: 10px;
        margin-top: 8px;
        border-radius: 30px;
        background: var(--Action-Orange, #dd6b17);
        color: #ffffff;
        font-family: Montserrat, sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 28px;
        text-decoration: none;
        border: 0;
        cursor: pointer;
        transition: background 0.2s ease-in-out;
    }

    .ten-years-cta:hover,
    .ten-years-cta:focus {
        background: #c25c11;
        color: #ffffff;
        text-decoration: none;
    }

    .ten-years-cta:focus-visible {
        outline: 2px solid var(--Trust-Green, #004d37);
        outline-offset: 3px;
    }

    /* ---------- Tablet ---------- */
    @media (max-width: 991px) {
        padding: 0 32px;

        .ten-years-inner {
            height: 290px;
            gap: 40px;
        }

        .ten-years-media {
            flex: 0 0 350px;
            width: 350px;
            height: 350px;
        }

        .ten-years-title {
            font-size: 32px;
            line-height: 42px;
        }
    }

    /* ---------- Mobile ---------- */
    /* Stacked layout: the circle sits fully inside the band, no clipping */
    @media (max-width: 767px) {
        padding: 32px 20px 36px;

        .ten-years-inner {
            flex-direction: column;
            height: auto;
            gap: 24px;
        }

        .ten-years-media {
            flex: 0 0 auto;
            width: 240px;
            height: 240px;
        }

        .ten-years-content {
            max-width: 100%;
        }

        .ten-years-title {
            font-size: 28px;
            line-height: 38px;
        }

        .ten-years-subtitle {
            font-size: 15px;
            line-height: 170%;
        }

        .ten-years-cta {
            padding: 10px 32px;
        }
    }
`;

const TenYearsBanner = ({ to = '/organizations?source=anniversary#newsletter-signup' }) => {
    const isExternal = /^(https?:)?\/\//.test(to) || to.startsWith('mailto:');

    return (
        <BannerSection>
            <div className="ten-years-inner">
                <div className={`ten-years-media${BANNER_IMAGE ? '' : ' ten-years-media--empty'}`}>
                    {BANNER_IMAGE ? (
                        <img
                            src={BANNER_IMAGE}
                            alt="Regenera community in the landscape"
                            width="420"
                            height="420"
                            loading="lazy"
                        />
                    ) : (
                        <span>Banner image placeholder</span>
                    )}
                </div>

                <div className="ten-years-content">
                    <h2 className="ten-years-title">
                        <FormattedMessage
                            id="NewLandingPage.tenYearsBanner.title"
                            values={{
                                br: <br />,
                            }}
                        />
                    </h2>
                    <p className="ten-years-subtitle">
                        <FormattedMessage id="NewLandingPage.tenYearsBanner.subTitle" />
                    </p>

                    {isExternal ? (
                        <a
                            className="ten-years-cta"
                            href={to}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FormattedMessage id="NewLandingPage.tenYearsBanner.cta" />
                        </a>
                    ) : (
                        <Link className="ten-years-cta" to={to}>
                            <FormattedMessage id="NewLandingPage.tenYearsBanner.cta" />
                        </Link>
                    )}
                </div>
            </div>
        </BannerSection>
    );
};

export default TenYearsBanner;
